import { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { LeafMark } from "@/components/LeafMark";
import { AdminAuthContext } from "./AdminAuthContext";
import { api } from "./api";
import { AdminShell } from "./AdminShell";
import { AdminLogin } from "./pages/AdminLogin";
import { AdminDashboard } from "./pages/AdminDashboard";
import { AdminCases } from "./pages/AdminCases";
import { AdminCaseDetail } from "./pages/AdminCaseDetail";
import { AdminPatients } from "./pages/AdminPatients";
import { AdminPatientDetail } from "./pages/AdminPatientDetail";
import { AdminHospitals } from "./pages/AdminHospitals";
import { AdminHospitalDetail } from "./pages/AdminHospitalDetail";
import { AdminSpecialties } from "./pages/AdminSpecialties";
import { AdminDestinations } from "./pages/AdminDestinations";
import { AdminCoordinators } from "./pages/AdminCoordinators";
import { AdminAnalytics } from "./pages/AdminAnalytics";
import { AdminSettings } from "./pages/AdminSettings";

export function AdminRoot() {
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);

    useEffect(() => {
        document.title = "UniversHeal Console";
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/auth/me");
                if (!cancelled) setUser(data);
            } catch {
                // not authenticated — login screen handles the rest
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const login = (u) => setUser(u);
    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {
            // clearing local state regardless
        }
        setUser(null);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#0B1513]">
                <LeafMark className="w-12 h-12 text-[#789B83] animate-pulse" />
            </div>
        );
    }

    if (!user) {
        return <AdminLogin onLogin={login} />;
    }

    return (
        <AdminAuthContext.Provider value={{ user, logout }}>
            <Routes>
                <Route path="/admin" element={<AdminShell />}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="cases" element={<AdminCases />} />
                    <Route path="cases/:caseId" element={<AdminCaseDetail />} />
                    <Route path="patients" element={<AdminPatients />} />
                    <Route path="patients/:patientId" element={<AdminPatientDetail />} />
                    <Route path="hospitals" element={<AdminHospitals />} />
                    <Route path="hospitals/:hospitalId" element={<AdminHospitalDetail />} />
                    <Route path="specialties" element={<AdminSpecialties />} />
                    <Route path="destinations" element={<AdminDestinations />} />
                    <Route path="analytics" element={<AdminAnalytics />} />
                    {user.role === "ADMIN" ? (
                        <>
                            <Route path="coordinators" element={<AdminCoordinators />} />
                            <Route path="settings" element={<AdminSettings />} />
                        </>
                    ) : null}
                    <Route path="login" element={<Navigate to="/admin" replace />} />
                    <Route path="*" element={<Navigate to="/admin" replace />} />
                </Route>
            </Routes>
        </AdminAuthContext.Provider>
    );
}
